import { useParams, useNavigate } from "react-router-dom";
import { useBenchState } from "./use-bench-state";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { ManageBench } from "./components/manage/manage";
import { useWallet } from "@txnlab/use-wallet";
import { useBenchAppId } from "./use-bench-appid";
import { clearBenchRegistryCache } from "./use-bench-registry";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { N } from "@/lib/utils";
import config from "@/config";
import { clearAlgoAcctInfoCache } from "@/hooks/use-algo-account";

export function BenchManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeAddress } = useWallet();

  const { data: appId } = useBenchAppId(id ? N(id) : undefined);
  const { isPending, error, refetch: refetchSingle, data } = useBenchState(appId ?? undefined, config.benchRegistryAppId);

  const refetch = () => {
    clearAlgoAcctInfoCache()
    refetchSingle();
    clearBenchRegistryCache();
  };

  const role =
    data?.ownerAddr === activeAddress
      ? "owner"
      : data?.feeAddr === activeAddress
      ? "fee manager"
      : undefined;

  return (
    <WithErrorAndLoading loading={isPending} error={error}>
      <div className="container flex flex-col gap-4 mx-auto max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-center gap-4 mb-6 relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/bench/${id}`)}
            className="p-2 absolute left-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex flex-col items-center">
            <h1 className="text-2xl font-bold">Manage Bench</h1>
            <p className="text-sm text-muted-foreground">
              Bench ID: {id}
            </p>
          </div>
        </div>

        {/* Management Panel */}
        {role ? (
          <ManageBench
            className="w-full"
            data={data!}
            reloadState={refetch}
          />
        ) : (
          <Card className="border-destructive/20 bg-destructive/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-destructive mb-2">
                    Access Denied
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    You don't have permission to manage this bench. Only the bench owner or fee manager can access the management interface.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/bench/${id}`)}
                    >
                      Back to Bench
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => navigate("/bench")}
                    >
                      View All Benches
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </WithErrorAndLoading>
  );
}
